// BM-H3978 (8 Oct 2026): the UAT mark, near the top of the matter page, for every matter type.
//
// The mark used to live only under the client care letter editor, at the bottom of the intake page, and
// the team could not find it. This reads the matter's UAT state by its reference (GET /matters/{ref}/uat-matter,
// which resolves the matter's intake record) and renders the one UatMatterToggle, so there is a single
// place that knows how marking works. A failed read says so; it never shows "real matter" in its place.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import UatMatterToggle from "./UatMatterToggle";

// matterOpen (4 Nov 2026, Vanessa: "what's the strange warning at the top?"): once file opening is
// complete the client care letter is signed, so the matter can no longer be marked. A real matter
// then shows no strip at all; a UAT matter keeps its badge so nobody mistakes it for a real one.
// client_signed only knows about letters signed through CaseMinder's own e-signing, which is why
// the matter's open state is checked as well.
export default function MatterUatStrip({ matterRef, matterOpen = false }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/matters/${encodeURIComponent(matterRef)}/uat-matter`);
      setState(data || {});
    } catch (err) {
      setState(null);
      setError(errorMessage(err, "Could not read whether this is a UAT matter"));
    }
  }, [matterRef]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="mb-4 rounded-lg px-4 py-2 text-xs bg-amber-50 text-amber-900" data-testid="matter-uat-strip-error">
        {error}. <button type="button" className="underline" onClick={load} data-testid="matter-uat-strip-retry">Try again</button>
      </div>
    );
  }
  if (!state) return null;
  if (state.uat_matter !== true && (matterOpen || state.client_signed === true)) return null;
  if (!state.found) {
    return (
      <div className="mb-4 rounded-lg px-4 py-2 text-xs bg-amber-50 text-slate-700" data-testid="matter-uat-strip-no-intake">
        This matter has no intake record, so it cannot be marked as a UAT matter from here.
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-lg overflow-hidden" data-testid="matter-uat-strip">
      <UatMatterToggle
        key={`${state.submission_id}-${state.uat_matter}`}
        submissionId={state.submission_id}
        uatMatter={state.uat_matter === true}
        markedBy={state.marked_by || ""}
        clientSigned={state.client_signed === true}
        onChange={load}
        compact
      />
    </div>
  );
}
